import { useEffect, useRef, useState } from "react";

interface Props {
  trackTitle: string;
  trackEmoji: string;
  trackColor: string;
  completedCount: number;
  totalCount: number;
  onClose: () => void;
}

const CERT_W = 800;
const CERT_H = 560;

function drawCertificate(
  canvas: HTMLCanvasElement,
  trackTitle: string,
  trackEmoji: string,
  trackColor: string,
  completedAt: string
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, CERT_W, CERT_H);

  // Left accent bar
  ctx.fillStyle = trackColor;
  ctx.fillRect(0, 0, 8, CERT_H);

  // Top-right decorative circle
  ctx.beginPath();
  ctx.arc(CERT_W + 60, -60, 180, 0, Math.PI * 2);
  ctx.fillStyle = `${trackColor}18`;
  ctx.fill();

  // Bottom-left decorative circle
  ctx.beginPath();
  ctx.arc(-40, CERT_H + 40, 150, 0, Math.PI * 2);
  ctx.fillStyle = "#191F2808";
  ctx.fill();

  // Border
  ctx.strokeStyle = "#E4E6EA";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(32, 32, CERT_W - 64, CERT_H - 64);

  // Corner decorations
  const corners = [[40, 40], [CERT_W - 40, 40], [40, CERT_H - 40], [CERT_W - 40, CERT_H - 40]];
  corners.forEach(([cx, cy]) => {
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fillStyle = trackColor;
    ctx.fill();
  });

  // GrowSeo Logo
  ctx.font = "bold 22px 'Arial'";
  ctx.fillStyle = "#191F28";
  ctx.textAlign = "left";
  ctx.fillText("GrowSeo", 64, 88);

  ctx.font = "12px Arial";
  ctx.fillStyle = "#8B95A1";
  ctx.fillText("AI Learning Platform", 64, 108);

  // Divider
  ctx.strokeStyle = "#E4E6EA";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(64, 124);
  ctx.lineTo(CERT_W - 64, 124);
  ctx.stroke();

  // Certificate label
  ctx.font = "bold 13px Arial";
  ctx.fillStyle = trackColor;
  ctx.textAlign = "center";
  ctx.fillText("CERTIFICATE OF COMPLETION", CERT_W / 2, 170);

  // Title
  ctx.font = "bold 42px Arial";
  ctx.fillStyle = "#191F28";
  ctx.textAlign = "center";
  ctx.fillText("수  료  증", CERT_W / 2, 232);

  // Subtitle
  ctx.font = "16px Arial";
  ctx.fillStyle = "#8B95A1";
  ctx.textAlign = "center";
  ctx.fillText("이 수료증은 아래 학습자가 과정을 성공적으로 완료하였음을 인증합니다.", CERT_W / 2, 272);

  // Recipient box
  ctx.fillStyle = "#F9F9F9";
  roundRect(ctx, CERT_W / 2 - 200, 296, 400, 72, 16);
  ctx.fill();

  ctx.font = "bold 28px Arial";
  ctx.fillStyle = "#191F28";
  ctx.textAlign = "center";
  ctx.fillText("GrowSeo 학습자", CERT_W / 2, 334);

  ctx.font = "13px Arial";
  ctx.fillStyle = "#8B95A1";
  ctx.textAlign = "center";
  ctx.fillText("Learner", CERT_W / 2, 356);

  // Track info
  ctx.font = "bold 18px Arial";
  ctx.fillStyle = "#191F28";
  ctx.textAlign = "center";
  ctx.fillText(`${trackEmoji}  ${trackTitle} 트랙`, CERT_W / 2, 412);

  // Thin accent underline
  ctx.strokeStyle = trackColor;
  ctx.lineWidth = 2.5;
  const textWidth = ctx.measureText(`${trackEmoji}  ${trackTitle} 트랙`).width;
  ctx.beginPath();
  ctx.moveTo(CERT_W / 2 - textWidth / 2, 422);
  ctx.lineTo(CERT_W / 2 + textWidth / 2, 422);
  ctx.stroke();

  // Date & Cert number row
  const today = completedAt;
  const certNo = `GS-${Date.now().toString().slice(-8)}`;

  ctx.font = "12px Arial";
  ctx.fillStyle = "#8B95A1";
  ctx.textAlign = "left";
  ctx.fillText(`발급일: ${today}`, 64, CERT_H - 64);

  ctx.textAlign = "right";
  ctx.fillText(`수료증 번호: ${certNo}`, CERT_W - 64, CERT_H - 64);

  // Seal circle
  ctx.beginPath();
  ctx.arc(CERT_W / 2, CERT_H - 58, 22, 0, Math.PI * 2);
  ctx.fillStyle = trackColor;
  ctx.fill();

  ctx.font = "bold 11px Arial";
  ctx.fillStyle = "#FFFFFF";
  ctx.textAlign = "center";
  ctx.fillText("인증", CERT_W / 2, CERT_H - 54);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export default function CertificateModal({ trackTitle, trackEmoji, trackColor, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [downloading, setDownloading] = useState(false);
  const completedAt = new Date().toLocaleDateString("ko-KR");

  useEffect(() => {
    if (!canvasRef.current) return;
    drawCertificate(canvasRef.current, trackTitle, trackEmoji, trackColor, completedAt);
  }, [trackTitle, trackEmoji, trackColor, completedAt]);

  const handleDownload = () => {
    if (!canvasRef.current) return;
    setDownloading(true);
    setTimeout(() => {
      const link = document.createElement("a");
      link.download = `GrowSeo_수료증_${trackTitle}.png`;
      link.href = canvasRef.current!.toDataURL("image/png");
      link.click();
      setDownloading(false);
    }, 300);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl overflow-hidden w-full max-w-2xl">
        {/* Header */}
        <div className="p-6 flex items-center justify-between border-b border-[#F2F4F6]">
          <div>
            <h3 className="text-xl font-black text-[#191F28]">수료증</h3>
            <p className="text-xs text-[#8B95A1] mt-0.5">{trackEmoji} {trackTitle} 트랙 완주 축하해요! 🎉</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F2F4F6] hover:bg-[#E8E9EB] transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-[#191F28] text-lg" />
          </button>
        </div>

        {/* Certificate Preview */}
        <div className="p-6 bg-[#F9F9F9]">
          <div className="overflow-hidden rounded-2xl border border-[#E4E6EA]" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}>
            <canvas
              ref={canvasRef}
              width={CERT_W}
              height={CERT_H}
              className="w-full h-auto block"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-0 flex gap-3">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex-1 py-4 rounded-2xl text-sm font-extrabold text-white transition-all cursor-pointer hover:opacity-90 flex items-center justify-center gap-2 whitespace-nowrap"
            style={{ backgroundColor: trackColor }}
          >
            {downloading ? (
              <><i className="ri-loader-4-line animate-spin" />다운로드 중...</>
            ) : (
              <><i className="ri-download-2-line text-base" />PNG 다운로드</>
            )}
          </button>
          <button
            onClick={() => {
              if (!canvasRef.current) return;
              const url = canvasRef.current.toDataURL("image/png");
              const img = new Image();
              img.src = url;
              const win = window.open("");
              if (win) {
                win.document.write(`<img src="${url}" style="width:100%;max-width:800px;" />`);
                win.document.title = `GrowSeo 수료증 - ${trackTitle}`;
              }
            }}
            className="px-6 py-4 rounded-2xl text-sm font-bold text-[#191F28] bg-[#F2F4F6] hover:bg-[#E8E9EB] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line mr-1" />새 탭에서 열기
          </button>
        </div>
      </div>
    </div>
  );
}
